/**
 * Query keys, built in one place.
 *
 * Hand-written arrays scattered through components are how cache invalidation
 * quietly stops working - `['issues']` in one file and `['issue']` in another
 * look the same in review and are different caches.
 *
 * The nesting means a broad key invalidates everything under it:
 * `queryClient.invalidateQueries({ queryKey: queryKeys.releases.all })` clears
 * both the version list and the deployment list, which is what you want after
 * publishing - a new version changes what a deployment can point at.
 */
export const queryKeys = {
  auth: {
    all: ['auth'] as const,
    me: () => [...queryKeys.auth.all, 'me'] as const,
    mfa: () => [...queryKeys.auth.all, 'mfa'] as const,
    sessions: () => [...queryKeys.auth.all, 'sessions'] as const,
  },
  dashboard: {
    all: ['dashboard'] as const,
    summary: (windowHours?: number) => [...queryKeys.dashboard.all, windowHours ?? null] as const,
  },
  logs: {
    all: ['logs'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.logs.all, 'list', filters ?? {}] as const,
  },
  crashes: {
    all: ['crashes'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.crashes.all, 'list', filters ?? {}] as const,
    groups: (filters?: Record<string, unknown>) =>
      [...queryKeys.crashes.all, 'groups', filters ?? {}] as const,
  },
  issues: {
    all: ['issues'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.issues.all, 'list', filters ?? {}] as const,
  },
  settings: {
    all: ['settings'] as const,
  },
  releases: {
    all: ['releases'] as const,
    versions: (filters?: Record<string, unknown>) =>
      [...queryKeys.releases.all, 'versions', filters ?? {}] as const,
    adoption: (days: number) => [...queryKeys.releases.all, 'adoption', days] as const,
    deployments: (filters?: Record<string, unknown>) =>
      [...queryKeys.releases.all, 'deployments', filters ?? {}] as const,
  },
} as const;
